import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money, Reveal } from "../lib/ui";

const ProductCard = ({ product, index }) => {
  const { addItem } = useCart();
  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <div
        data-testid={`product-card-${product.slug}`}
        className="group relative flex h-full flex-col border border-line bg-sand transition-colors duration-300 hover:border-ink"
      >
        <Link to={`/product/${product.slug}`} className="relative block overflow-hidden bg-surface">
          <div className="absolute inset-0 spotlight z-[1]" />
          <div className="flex aspect-[4/3] items-center justify-center overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
          </div>
          <span className="absolute left-4 top-4 z-[3] font-mono text-xs tracking-widest text-ink/50">
            {String(product.rank).padStart(2, "0")}
          </span>
          {product.featured && (
            <span className="absolute right-4 top-4 z-[3] bg-rust px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-cream">
              Top Seller
            </span>
          )}
        </Link>

        <div className="flex flex-1 flex-col p-6">
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust">{product.category}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">{product.purity} pure</p>
          </div>
          <Link to={`/product/${product.slug}`}>
            <h3 className="mt-2 font-display text-3xl leading-none tracking-tight text-ink transition-colors duration-300 group-hover:text-rust">
              {product.name}
            </h3>
          </Link>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45">{product.abbreviation}</p>
          <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-ink/70">{product.tagline} — {product.size} vial.</p>

          <div className="mt-6 flex items-center justify-between border-t border-line pt-5">
            <span className="font-mono text-xl text-ink">{money(product.price)}</span>
            <div className="flex items-center gap-2">
              <Link
                to={`/product/${product.slug}`}
                data-testid={`view-coa-${product.slug}`}
                className="flex items-center gap-1.5 border border-line px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-ink"
              >
                <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> COA
              </Link>
              <button
                data-testid={`add-to-cart-${product.slug}`}
                onClick={() => addItem(product)}
                className="flex items-center gap-1.5 border border-ink bg-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-rust hover:border-rust"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

const COLLECTION_META = [
  { key: "healers", title: "Healers", blurb: "Tissue-repair, recovery & complexion research." },
  { key: "builders", title: "Builders", blurb: "Growth, energy & longevity research." },
  { key: "burners", title: "Burners", blurb: "Metabolic & body-composition research." },
  { key: "lovers", title: "Lovers", blurb: "Libido & central-nervous-system research." },
];

export const ProductGrid = ({ products }) => (
  <section id="catalog" data-testid="product-grid" className="scroll-mt-32 bg-sand px-6 py-24 md:px-12 md:py-32">
    <div className="mx-auto max-w-[1500px]">
      <div className="mb-10 flex flex-col justify-between gap-6 border-b border-line pb-8 md:flex-row md:items-end">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-rust">[ The Collection ]</p>
          <h2 className="mt-4 font-display text-5xl leading-[0.9] tracking-tighter text-ink md:text-7xl">
            The<br />Vault
          </h2>
        </div>
        <p className="max-w-xs font-body text-sm leading-relaxed text-ink/60">
          The most-requested research peptides — each third-party verified by HPLC and mass-spec, with a
          Certificate of Analysis in every order.
        </p>
      </div>

      <div data-testid="collections-disclaimer" className="mb-16 border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.12em] text-ink/60">
          ⚠ Collections group peptides by common area of research interest only. They are not claims of any
          benefit, effect, or outcome. Every product is sold strictly for Research Use Only.
        </p>
      </div>

      <div className="space-y-20">
        {COLLECTION_META.map((col) => {
          const items = products.filter((p) => p.collection === col.key);
          if (items.length === 0) return null;
          return (
            <div key={col.key} data-testid={`collection-${col.key}`}>
              <div className="mb-8 flex flex-col gap-2 border-b border-line pb-5 md:flex-row md:items-end md:justify-between">
                <h3 className="font-display text-4xl leading-none tracking-tight text-ink md:text-5xl">
                  {col.title}
                  <span className="ml-3 font-mono text-sm align-middle text-ink/30">[{String(items.length).padStart(2, "0")}]</span>
                </h3>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-rust">{col.blurb}</p>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
